import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HelpTooltip from './ui/HelpTooltip';
import { compareNames, matchesQuery } from '../utils/text';
import './MultiSelect.css';

/**
 * Candidate rows rendered before the dropdown stops and asks the user to narrow.
 * A silent scroll box gives no hint that more exist.
 */
const OPTION_RENDER_CAP = 50;

export type SelectId = string | number;

export interface MultiSelectOption {
  _id: SelectId;
  name: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedIds: SelectId[];
  onChange: (selectedIds: SelectId[]) => void;
  placeholder?: string;
  label?: string;
  /** Help text shown on label hover (matches FormField labelHelpText pattern) */
  labelHelpText?: string;
  id?: string;
}

// Loose comparison so "1" matches 1
const idEq = (a: SelectId, b: SelectId) => String(a) === String(b);
const idIn = (list: SelectId[], value: SelectId) => list.some(id => idEq(id, value));

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedIds,
  onChange,
  placeholder,
  label,
  labelHelpText,
  id,
}) => {
  const { t } = useTranslation();
  const defaultPlaceholder = placeholder || t('common.selectItems');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeDescendant, setActiveDescendant] = useState<SelectId | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = id ? `${id}-listbox` : 'multiselect-listbox';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Always A→Z. `options` arrives in API order, so without this a name sits
  // wherever the server happened to return it — unfindable past ~20 chips.
  const selectedOptions = options
    .filter((opt) => idIn(selectedIds, opt._id))
    .slice()
    .sort((a, b) => compareNames(a.name, b.name));

  // Diacritics-insensitive: "cerny" must match "Černý".
  const matchingOptions = options
    .filter((opt) => !idIn(selectedIds, opt._id) && matchesQuery(opt.name, search))
    .slice()
    .sort((a, b) => compareNames(a.name, b.name));

  const availableOptions = matchingOptions.slice(0, OPTION_RENDER_CAP);
  const hiddenCount = matchingOptions.length - availableOptions.length;

  const handleSelect = (optionId: SelectId) => {
    onChange([...selectedIds, optionId]);
    setSearch('');
    inputRef.current?.focus();
  };

  const handleRemove = (optionId: SelectId) => {
    onChange(selectedIds.filter((sid) => !idEq(sid, optionId)));
  };

  // Reset activeDescendant when available options change (e.g., from search filtering)
  useEffect(() => {
    if (activeDescendant != null && !availableOptions.some((opt) => idEq(opt._id, activeDescendant))) {
      setActiveDescendant(null);
    }
  }, [availableOptions, activeDescendant]);

  // Scroll active option into view
  useEffect(() => {
    if (activeDescendant != null) {
      document.getElementById(`option-${activeDescendant}`)?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeDescendant]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && search === '' && selectedIds.length > 0) {
      // Remove the last selected item
      onChange(selectedIds.slice(0, -1));
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setActiveDescendant(null);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        // Open dropdown if closed
        setIsOpen(true);
        if (availableOptions.length > 0) {
          setActiveDescendant(availableOptions[0]._id);
        }
      } else if (availableOptions.length > 0) {
        // Move to next option
        const currentIndex = activeDescendant != null
          ? availableOptions.findIndex((opt) => idEq(opt._id, activeDescendant))
          : -1;
        const nextIndex = currentIndex < availableOptions.length - 1 ? currentIndex + 1 : 0;
        setActiveDescendant(availableOptions[nextIndex]._id);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        // Open dropdown if closed
        setIsOpen(true);
        if (availableOptions.length > 0) {
          setActiveDescendant(availableOptions[availableOptions.length - 1]._id);
        }
      } else if (availableOptions.length > 0) {
        // Move to previous option
        const currentIndex = activeDescendant != null
          ? availableOptions.findIndex((opt) => idEq(opt._id, activeDescendant))
          : 0;
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : availableOptions.length - 1;
        setActiveDescendant(availableOptions[prevIndex]._id);
      }
    } else if (e.key === 'Home' && isOpen && availableOptions.length > 0) {
      // Move to first option
      e.preventDefault();
      setActiveDescendant(availableOptions[0]._id);
    } else if (e.key === 'End' && isOpen && availableOptions.length > 0) {
      // Move to last option
      e.preventDefault();
      setActiveDescendant(availableOptions[availableOptions.length - 1]._id);
    } else if (e.key === 'Enter' && availableOptions.length > 0) {
      e.preventDefault();
      const selectedId = activeDescendant ?? availableOptions[0]._id;
      handleSelect(selectedId);
      setActiveDescendant(null);
    }
  };

  return (
    <div className="multi-select-wrapper">
      {label && (
        <label htmlFor={id} className="multi-select-label">
          <HelpTooltip helpText={labelHelpText}>
            {selectedOptions.length > 0 ? `${label} (${selectedOptions.length})` : label}
          </HelpTooltip>
        </label>
      )}
      <div
        ref={containerRef}
        className={`multi-select-container ${isOpen ? 'open' : ''}`}
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        <div className="multi-select-input-area">
          {selectedOptions.map((option) => (
            <span key={String(option._id)} className="multi-select-chip">
              {option.name}
              <button
                type="button"
                className="multi-select-chip-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(option._id);
                }}
                aria-label={`Remove ${option.name}`}
              >
                &times;
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            id={id}
            type="text"
            className="multi-select-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={selectedOptions.length === 0 ? defaultPlaceholder : ''}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-activedescendant={activeDescendant != null ? `option-${activeDescendant}` : undefined}
            aria-autocomplete="list"
          />
        </div>
        <div className="multi-select-arrow">
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path
              d="M2 4 L6 8 L10 4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        {isOpen && (
          <div
            className="multi-select-dropdown"
            role="listbox"
            id={listboxId}
            aria-label={label || t('common.selectItems')}
          >
            {availableOptions.length === 0 ? (
              <div className="multi-select-no-options" role="status">
                {search ? t('common.noMatches') : t('common.noMoreOptions')}
              </div>
            ) : (
              <>
              {hiddenCount > 0 && (
                <div className="multi-select-more" role="status">
                  {t('common.showingOfKeepTyping', {
                    shown: availableOptions.length,
                    total: matchingOptions.length,
                  })}
                </div>
              )}
              {availableOptions.map((option) => (
                <div
                  key={String(option._id)}
                  id={`option-${option._id}`}
                  className={`multi-select-option${activeDescendant != null && idEq(activeDescendant, option._id) ? ' active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(option._id);
                    setActiveDescendant(null);
                  }}
                  role="option"
                  aria-selected={idIn(selectedIds, option._id)}
                >
                  {option.name}
                </div>
              ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;
